import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Flex, Button, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { setSelectedConnector, setNodeType, setInsertNodeBetween, setNodeToBeDeleted } from '../../../redux/slices/connectorSlice';
import SvgIcons from '../../layout/assets/SvgIcons';

const WorkflowSidebar = ({ isOpen, onClose, selectedData, selectedType }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [searchTerm, setSearchTerm] = useState('');
    const [shouldRender, setShouldRender] = useState(isOpen);
    const [isVisible, setIsVisible] = useState(false);
    const animationSeed = `${selectedType || 'none'}-${selectedData?.edgeId || selectedData?.id || 'empty'}`;
    const { workflow } = useSelector(state => state.connector);

    // The "+" connector is removable when its source node forks into more than one branch
    // (i.e. it is a parallel-branch sibling, not the sole continuation of a chain).
    const canDeleteConnector = useMemo(() => {
        const connectorId = selectedData?.id
        const edges = workflow?.edges
        if (!connectorId || !Array.isArray(edges)) return false
        const incomingEdge = edges.find(e => e.target === connectorId)
        if (!incomingEdge) return false
        return edges.filter(e => e.source === incomingEdge.source).length > 1
    }, [selectedData?.id, workflow?.edges])

    const isStartOnlyWorkflow = useMemo(() => {
        const nodes = workflow?.nodes;
        if (!Array.isArray(nodes) || nodes.length === 0) return false;
        // Start nodes are represented as type: 'default' with data.type === 'start'
        const nonStartNodes = nodes.filter(n => (n?.data?.type ?? n?.type) !== 'start');
        return nonStartNodes.length === 0;
    }, [workflow?.nodes]);

    useEffect(() => {
        if (selectedType === "circle") {
            // Handle circle connector logic if needed
        }
    }, [selectedData, selectedType, isOpen]);

    useEffect(() => {
        let frameId;
        let timeoutId;

        if (isOpen) {
            setShouldRender(true);
            setIsVisible(false);

            frameId = requestAnimationFrame(() => {
                frameId = requestAnimationFrame(() => {
                    setIsVisible(true);
                });
            });

            return () => {
                if (frameId) cancelAnimationFrame(frameId);
            };
        }

        setIsVisible(false);
        timeoutId = setTimeout(() => {
            setShouldRender(false);
        }, 280);

        return () => {
            if (frameId) cancelAnimationFrame(frameId);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isOpen, animationSeed]);

    const handleTypeClick = (nodeType, selectedConnectorData = selectedData) => {

        // Remove the clicked "+" connector (parallel-branch sibling)
        if (nodeType === 'deleteConnector') {
            if (selectedConnectorData?.id) dispatch(setNodeToBeDeleted(selectedConnectorData.id));
            onClose();
            return;
        }

        // Define implemented node types
        const implementedNodeTypes = ['step', 'approvalStep', 'end', 'notification', 'httpRequest', 'sharePublicForm', 'duplicateCheck', 'outlookCalendar', 'googlePlaces', 'parallelBranch', 'mergeFlow'];

        // Check if the clicked node type is implemented
        if (!implementedNodeTypes.includes(nodeType)) {
            return; // Don't close sidebar or dispatch actions
        }

        // Handle edge insertion (when clicked from edge hover)
        if (selectedType === 'insertNode' && selectedConnectorData.edgeId) {
            // For edge insertion, we need to create the node and update edges
            dispatch(setInsertNodeBetween({
                edgeId: selectedConnectorData.edgeId,
                sourceNodeId: selectedConnectorData.sourceNodeId,
                targetNodeId: selectedConnectorData.targetNodeId,
                nodeType: nodeType,
                position: selectedConnectorData.insertPosition
            }));
            onClose();
            return; // after edge insertion we exit
        }

        // Validate end step connection
        // if (nodeType === "end" && selectedConnectorData) {
        //     let prevEdge = workflow.edges.find(edge => edge.target === selectedConnectorData.id);
        //     let prevNode = prevEdge ? workflow.nodes.find(node => node.id === prevEdge.source) : null;
        //     if (!prevNode?.data?.approvalStep) {
        //         return;
        //     }
        // }

        dispatch(setSelectedConnector(selectedConnectorData));
        dispatch(setNodeType(nodeType));
        onClose();
    };

    if (!shouldRender) return null;

    return (
        <div className={`workflow-sidebar ${isVisible ? 'workflow-sidebar--open' : 'workflow-sidebar--closing'}`}>
            <div className="p-4 bg-white border-b border-gray-200">
                <Flex justify="space-between" align="center">
                    <h3 className="text-lg font-medium text-gray-800 m-0">
                        {t("Workflow Tools")}
                    </h3>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={onClose}
                        size="large"
                        className="text-gray-500 hover:text-gray-700"
                    />
                </Flex>
            </div>

            <div className="workflow-sidebar-body">
                <div className='flex gap-3 mb-4'>
                    {(isStartOnlyWorkflow
                        ? [{ k: 'step', label: t('Workflow Step'), icon: <SvgIcons type="workflowStep" className='w-4 h-4' /> }]
                        : selectedType === 'insertNode'
                            ? [{ k: 'step', label: t('Workflow Step'), icon: <SvgIcons type="workflowStep" className='w-4 h-4' /> }, { k: 'approvalStep', label: t('Approval Step'), icon: <SvgIcons type="approvalStep" className='w-4 h-4' /> }]
                            : [{ k: 'step', label: t('Workflow Step'), icon: <SvgIcons type="workflowStep" className='w-4 h-4' /> }, { k: 'approvalStep', label: t('Approval Step'), icon: <SvgIcons type="approvalStep" className='w-4 h-4' /> }, { k: 'end', label: t('End Workflow'), icon: <SvgIcons type="workflowEnd" className='w-4 h-4' /> }]
                    ).map(seg => (
                        <button
                            key={seg.k}
                            onClick={() => handleTypeClick(seg.k, selectedData)}
                            className='flex-1 border rounded-lg px-2 py-2 text-[11px] flex flex-col items-center gap-1 hover:border-indigo-400 transition-colors bg-white'
                        >
                            <span className='w-7 h-7 flex items-center justify-center rounded-md bg-indigo-50 text-indigo-600'>{seg.icon}</span>
                            <span className='font-medium text-gray-700'>{seg.label}</span>
                        </button>
                    ))}
                </div>

                {!isStartOnlyWorkflow && (
                    <>
                        <Input
                            allowClear
                            placeholder={t('Search')}
                            prefix={<SearchOutlined className='text-gray-400' />}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className='rounded-md mb-4'
                        />
                        {/* {renderContent()} */}
                        <div className='space-y-1 flex flex-col gap-4'>
                            <HumanInLoopSection handleTypeClick={handleTypeClick} searchTerm={searchTerm} />
                            {/* <Integration handleTypeClick={handleTypeClick} searchTerm={searchTerm} /> */}
                            {/* {selectedType !== 'insertNode' && <WorkflowControl handleTypeClick={handleTypeClick} searchTerm={searchTerm} canDeleteConnector={canDeleteConnector} />} */}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default WorkflowSidebar;

const HumanInLoopSection = ({ handleTypeClick, searchTerm = '' }) => {
    const [expanded, setExpanded] = useState(false)
    const { t } = useTranslation();

    const items = [
        { key: 'approvalStep', label: t('Approval Step') },
        { key: 'step', label: t('Workflow Step') }
    ]

    const filteredItems = items.filter(item =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const categoryMatches = 'Human In Loop'.toLowerCase().includes(searchTerm.toLowerCase())

    // Hide entire section if search doesn't match category name or any items
    if (searchTerm && !categoryMatches && filteredItems.length === 0) {
        return null
    }

    // Show all items if category name matches, otherwise show filtered items
    const displayItems = categoryMatches ? items : filteredItems

    // Auto-expand when there's a search term and we have matching items
    const shouldExpand = searchTerm ? (filteredItems.length > 0 || categoryMatches) : expanded

    return (
        <div className='workflow-tool'>
            <div className='tool-header' onClick={() => setExpanded(!expanded)}>
                <div className='flex items-center gap-3'>
                    <div className="text-[#0A99FE] icon-block" ><SvgIcons type="humanInLoop" className='w-4 h-4' /></div>
                    <div className='text-black'> {t('Human In Loop')}</div>
                </div>
                {
                    shouldExpand ? <ChevronDown className='text-gray-400 w-4 h-4 cursor-pointer mr-2' /> : <ChevronRight className='text-gray-400 w-4 h-4  cursor-pointer mr-2' />
                }
            </div>
            {
                shouldExpand ?
                    <div className='flex flex-col gap-3 mt-4'>
                        {displayItems.map(item => (
                            <div key={item.key} className='workflow-sub-category' onClick={() => handleTypeClick(item.key)}>{item.label}</div>
                        ))}
                    </div>
                    : null
            }
        </div>
    )
}


const WorkflowControl = ({ handleTypeClick, searchTerm = '', canDeleteConnector = false }) => {
    const [expanded, setExpanded] = useState(false)
    const { t } = useTranslation();

    const items = [
        { key: 'parallelBranch', label: t('Parallel Branch') },
        { key: 'mergeFlow', label: t('Merge Flow') },
        { key: 'end', label: t('End') },
        ...(canDeleteConnector ? [{ key: 'deleteConnector', label: t('Delete Connector') }] : [])
    ]

    const filteredItems = items.filter(item =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const categoryMatches = 'Workflow Control'.toLowerCase().includes(searchTerm.toLowerCase())

    // Hide entire section if search doesn't match category name or any items
    if (searchTerm && !categoryMatches && filteredItems.length === 0) {
        return null
    }

    // Show all items if category name matches, otherwise show filtered items
    const displayItems = categoryMatches ? items : filteredItems

    // Auto-expand when there's a search term and we have matching items
    const shouldExpand = searchTerm ? (filteredItems.length > 0 || categoryMatches) : expanded

    return (
        <div className='workflow-tool'>
            <div className='tool-header' onClick={() => setExpanded(!expanded)}>
                <div className='flex items-center gap-3'>
                    <div className="text-[#0A99FE] icon-block" ><SvgIcons type="workflowControl" className='w-4 h-4' /></div>
                    <div className='text-black'> {t("Workflow Control")}</div>
                </div>
                {
                    shouldExpand ? <ChevronDown className='text-gray-400 w-4 h-4 cursor-pointer mr-2' onClick={() => setExpanded(false)} /> : <ChevronRight className='text-gray-400 w-4 h-4  cursor-pointer mr-2' onClick={() => setExpanded(true)} />
                }
            </div>
            {
                shouldExpand ?
                    <div className='flex flex-col gap-3 mt-4'>
                        {displayItems.map(item => (
                            <div key={item.key} className='workflow-sub-category' onClick={() => handleTypeClick(item.key)}>{item.label}</div>
                        ))}
                    </div>
                    : null
            }
        </div>
    )
}

const Integration = ({ handleTypeClick, searchTerm = '' }) => {
    const [expanded, setExpanded] = useState(false)
    const { t } = useTranslation();

    const items = [
        { key: 'sharePublicForm', label: t('Share Public Form') },
        { key: 'duplicateCheck', label: t('Duplicate Check') },
        { key: 'outlookCalendar', label: t('Outlook Calendar') },
        { key: 'googlePlaces', label: t('Google Places') }
    ]

    const filteredItems = items.filter(item =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const categoryMatches = 'Integration'.toLowerCase().includes(searchTerm.toLowerCase())

    // Hide entire section if search doesn't match category name or any items
    if (searchTerm && !categoryMatches && filteredItems.length === 0) {
        return null
    }

    // Show all items if category name matches, otherwise show filtered items
    const displayItems = categoryMatches ? items : filteredItems

    // Auto-expand when there's a search term and we have matching items
    const shouldExpand = searchTerm ? (filteredItems.length > 0 || categoryMatches) : expanded

    return (
        <div className='workflow-tool'>
            <div className='tool-header' onClick={() => setExpanded(!expanded)}>
                <div className='flex items-center gap-3'>
                    <div className="text-[#0A99FE] icon-block" ><SvgIcons type="integration" className='w-4 h-4' /></div>
                    <div className='text-black'> {t("Integration")}</div>
                </div>
                {
                    shouldExpand ? <ChevronDown className='text-gray-400 w-4 h-4 cursor-pointer mr-2' onClick={() => setExpanded(false)} /> : <ChevronRight className='text-gray-400 w-4 h-4  cursor-pointer mr-2' onClick={() => setExpanded(true)} />
                }
            </div>
            {
                shouldExpand ?
                    <div className='flex flex-col gap-3 mt-4'>
                        {displayItems.map(item => (
                            <div key={item.key} className='workflow-sub-category' onClick={() => handleTypeClick(item.key)}>{item.label}</div>
                        ))}
                    </div>
                    : null
            }
        </div>
    )
}
